import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect } from 'react';

const MailAdmin = () => {
  const [inquiries, setInquiries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [message, setMessage] = useState('');
  const [templates, setTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ title: '', body: '' });
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [listOpen, setListOpen] = useState(true);
  const [visibleCount, setVisibleCount] = useState(10);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    adminFetch('/api/admin-mail.php?action=list')
      .then((r) => r.ok ? r.json() : { inquiries: [] })
      .then((data) => setInquiries(data.inquiries || []))
      .catch(() => setInquiries([]));
    adminFetch('/api/admin-read.php?file=templates&t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, []);

  const fetchMail = async () => {
    setFetching(true);
    setMessage('');
    try {
      const res = await adminFetch('/api/admin-mail.php?action=fetch');
      const data = await res.json();
      if (data.success) {
        setMessage(data.newCount > 0 ? `${data.newCount}件の新着メールを取り込みました` : '新着メールはありません');
        // 一覧を再取得
        const listRes = await adminFetch('/api/admin-mail.php?action=list');
        const listData = await listRes.json();
        setInquiries(listData.inquiries || []);
      } else {
        setMessage('受信チェックに失敗しました: ' + (data.message || ''));
      }
    } catch {
      setMessage('受信チェックに失敗しました');
    } finally {
      setFetching(false);
    }
  };

  const saveTemplate = async () => {
    if (!newTemplate.title.trim() || !newTemplate.body.trim()) return;
    const updated = [...templates, { id: Date.now(), ...newTemplate }];
    try {
      await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'templates', data: updated }),
      });
      setTemplates(updated);
      setNewTemplate({ title: '', body: '' });
      setShowTemplateForm(false);
    } catch {}
  };

  const deleteTemplate = async (id) => {
    const updated = templates.filter((t) => t.id !== id);
    try {
      await adminFetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'templates', data: updated }),
      });
      setTemplates(updated);
    } catch {}
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('このメールを削除しますか？')) return;
    try {
      await adminFetch('/api/admin-mail.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      setInquiries((prev) => prev.filter((item) => item.id !== id));
      if (selected?.id === id) setSelected(null);
      setMessage('削除しました');
    } catch {
      setMessage('削除に失敗しました');
    }
  };

  const openDetail = (item) => {
    setSelected(item);
    setReplyText('');
    setAttachments([]);
    setMessage('');
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('to', selected.email);
      formData.append('name', selected.name);
      formData.append('subject', 'Re: 【SrWith】お問い合わせについて');
      formData.append('body', replyText);
      formData.append('inquiryId', selected.id);
      attachments.forEach((file) => formData.append('attachments[]', file));

      const res = await adminFetch('/api/admin-mail.php?action=reply', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setMessage('返信しました');
        const fileNames = attachments.map((f) => f.name);
        const newReply = { date: new Date().toISOString(), body: replyText, attachments: fileNames };
        setReplyText('');
        setAttachments([]);
        setInquiries((prev) =>
          prev.map((item) =>
            item.id === selected.id
              ? { ...item, replied: true, replies: [...(item.replies || []), newReply] }
              : item
          )
        );
        setSelected((prev) => ({
          ...prev,
          replied: true,
          replies: [...(prev.replies || []), newReply],
        }));
      } else {
        setMessage('送信に失敗しました: ' + (data.message || ''));
      }
    } catch {
      setMessage('送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ja-JP') + ' ' + d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">メール管理</h2>
        <button
          onClick={fetchMail}
          disabled={fetching}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
        >
          {fetching ? 'チェック中...' : 'メール受信チェック'}
        </button>
      </div>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      <div className="flex gap-4">
        {/* 一覧 */}
        <div className={`shrink-0 transition-all duration-300 ${listOpen ? 'w-[20%]' : 'w-10'}`}>
          {listOpen ? (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                <span className="text-xs font-bold text-gray-500">{inquiries.length}件</span>
                <button onClick={() => setListOpen(false)} className="text-gray-400 hover:text-gray-600 text-sm">&#x2039;</button>
              </div>
              {inquiries.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-xs">なし</div>
              ) : (
                <>
                  <div className="divide-y max-h-[70vh] overflow-y-auto">
                    {inquiries.slice(0, visibleCount).map((item) => (
                      <button
                        key={item.id}
                        onClick={() => openDetail(item)}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${
                          selected?.id === item.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-1 mb-0.5">
                          {!item.replied && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full shrink-0" />}
                          <span className="text-[10px] text-gray-400 truncate">{formatDate(item.date)}</span>
                        </div>
                        <p className="font-bold text-xs truncate">{item.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">{item.purposeLabel || item.type}</p>
                      </button>
                    ))}
                  </div>
                  {inquiries.length > visibleCount && (
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 10)}
                      className="w-full py-2 text-xs text-blue-600 font-bold hover:bg-blue-50 border-t"
                    >
                      もっと見る
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <button
              onClick={() => setListOpen(true)}
              className="bg-white rounded-xl shadow w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600"
            >
              &#x203A;
            </button>
          )}
        </div>

        {/* 詳細・返信 */}
        <div className="flex-1">
          {selected ? (
            <div className="bg-white rounded-xl shadow p-6">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-lg mb-2">{selected.name}</h3>
                  <p className="text-sm text-gray-500">{selected.email}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(selected.date)}</p>
                </div>
                <button
                  onClick={() => deleteInquiry(selected.id)}
                  className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100 shrink-0"
                >
                  削除
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm whitespace-pre-wrap">
                {selected.details}
              </div>

              {/* 返信履歴 */}
              {selected.replies && selected.replies.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-bold text-gray-600 mb-2">返信履歴</p>
                  <div className="space-y-2">
                    {selected.replies.map((reply, i) => {
                      const isCustomer = reply.from === 'customer';
                      return (
                        <div key={i} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${isCustomer ? 'bg-white border border-gray-200 rounded-tl-sm' : 'bg-blue-500 text-white rounded-tr-sm'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-bold ${isCustomer ? 'text-gray-600' : 'text-blue-100'}`}>
                                {isCustomer ? 'お客様' : 'SrWith事務局'}
                              </span>
                              <span className={`text-xs ${isCustomer ? 'text-gray-400' : 'text-blue-200'}`}>{formatDate(reply.date)}</span>
                            </div>
                            {reply.subject && <p className={`text-xs mb-1 ${isCustomer ? 'text-gray-500' : 'text-blue-200'}`}>件名: {reply.subject}</p>}
                            <p className="text-sm whitespace-pre-wrap break-words">{reply.body}</p>
                            {reply.attachments && reply.attachments.length > 0 && (
                              <div className={`mt-1.5 flex flex-wrap gap-1 ${isCustomer ? 'text-gray-500' : 'text-blue-200'}`}>
                                {reply.attachments.map((name, j) => (
                                  <span key={j} className="text-xs flex items-center gap-0.5">📎 {name}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <label className="block text-sm font-bold mb-2">返信</label>
                {templates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {templates.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setReplyText((prev) => prev + t.body)}
                        className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded text-xs font-bold hover:bg-gray-200"
                        title={t.body}
                      >
                        {t.title}
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={6}
                  placeholder="返信内容を入力..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                {/* 添付ファイル */}
                <div className="mt-2">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-gray-500 hover:text-gray-700 text-sm">
                    <span>📎</span>
                    <span className="text-xs font-bold">ファイルを添付</span>
                    <span className="text-[10px] text-gray-400 font-normal">（30MBまで）</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => setAttachments((prev) => [...prev, ...Array.from(e.target.files)])}
                    />
                  </label>
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {attachments.map((file, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs text-gray-700">
                          📎 {file.name}
                          <button
                            onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                            className="text-red-400 hover:text-red-600 font-bold ml-0.5"
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={sendReply}
                  disabled={sending || !replyText.trim()}
                  className="mt-3 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
                >
                  {sending ? '送信中...' : '返信を送信'}
                </button>
              </div>

              {/* 定型文管理 */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-gray-600">定型文</p>
                  <button
                    onClick={() => setShowTemplateForm(!showTemplateForm)}
                    className="text-xs text-blue-600 font-bold hover:underline"
                  >
                    {showTemplateForm ? '閉じる' : '+ 追加'}
                  </button>
                </div>
                {showTemplateForm && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-2">
                    <input
                      type="text"
                      value={newTemplate.title}
                      onChange={(e) => setNewTemplate((p) => ({ ...p, title: e.target.value }))}
                      placeholder="ボタン名（例: お礼）"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <textarea
                      value={newTemplate.body}
                      onChange={(e) => setNewTemplate((p) => ({ ...p, body: e.target.value }))}
                      placeholder="定型文の内容"
                      rows={3}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                    <button
                      onClick={saveTemplate}
                      className="bg-blue-600 text-white px-4 py-1.5 rounded text-xs font-bold hover:bg-blue-700"
                    >
                      保存
                    </button>
                  </div>
                )}
                {templates.length > 0 && (
                  <div className="space-y-1">
                    {templates.map((t) => (
                      <div key={t.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-gray-700">{t.title}</p>
                          <p className="text-xs text-gray-400 truncate">{t.body}</p>
                        </div>
                        <button
                          onClick={() => deleteTemplate(t.id)}
                          className="text-red-500 text-xs font-bold hover:text-red-700 shrink-0 ml-2"
                        >
                          削除
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
              左の一覧から選択してください
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MailAdmin;
